import { caseRouteSuffix, crumbsFromPath, routeIdentity, type Crumb } from './breadcrumbs';
import { reviewDocumentMode } from './reviewDocumentBreadcrumb';
import { CRUMB_SLOTS, type CrumbContribution, type CrumbSlot } from './breadcrumbTypes';

export interface TrailInput {
  pathname: string;
  search: string;
  caseKey: string | null;
  caseName: string | null;
  reviewSource: 'production' | 'batch';
  contributions: readonly CrumbContribution[];
}

export interface CollectionSelection {
  setId: string | null;
  collectionId: string | null;
  custodian: string | null;
  collectionSetId: string | null;
}

const nonempty = (value: string | null) => value === null || value === '' ? null : value;

export function hasCollectionScope(search: string): boolean {
  const params = new URLSearchParams(search);
  return nonempty(params.get('collection')) !== null || nonempty(params.get('set')) !== null;
}

const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

function decodeId(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export type ChromeOwner = 'shell' | 'screen';

// React Router matches static segments case-insensitively and after percent-decoding,
// so a raw case-sensitive comparison would disagree with the route that actually mounts.
const staticSegment = (raw: string, literal: string) => decodeId(raw).toLowerCase() === literal;

export function chromeOwner(pathname: string, search: string, caseKey: string): ChromeOwner {
  const [empty, cases, key, collection, ...rest] = normalize(pathname).split('/');
  return empty === '' &&
    rest.length === 0 &&
    staticSegment(cases ?? '', 'cases') &&
    staticSegment(collection ?? '', 'collection') &&
    decodeId(key ?? '') === caseKey &&
    hasCollectionScope(search)
    ? 'screen'
    : 'shell';
}

export function scopedHref(pathname: string, search: string, keys: readonly string[]): string {
  const source = new URLSearchParams(search);
  const target = new URLSearchParams();
  for (const key of keys) {
    const value = nonempty(source.get(key));
    if (value !== null) target.set(key, value);
  }
  return `${normalize(pathname)}${target.size ? `?${target.toString()}` : ''}`;
}

export function collectionSelection(search: string): CollectionSelection {
  const params = new URLSearchParams(search);
  return {
    setId: nonempty(params.get('set')),
    collectionId: nonempty(params.get('collection')),
    custodian: nonempty(params.get('custodian')),
    collectionSetId: nonempty(params.get('collectionSet')),
  };
}

type Node = Crumb & {
  slot?: CrumbSlot;
  representsLocation?: boolean;
};

function mergeNodes(
  nodes: Node[],
  contributions: readonly CrumbContribution[],
): Crumb[] {
  const result: Node[] = nodes.map(node => ({ ...node, current: undefined }));
  for (const slot of CRUMB_SLOTS) {
    const matches = contributions.filter(contribution => contribution.slot === slot && contribution.label.trim());
    if (matches.length !== 1) continue;
    const contribution = matches[0];
    const existing = result.findIndex(node => node.slot === slot);
    if (existing >= 0) {
      result[existing] = { ...result[existing], label: contribution.label };
    }
  }
  return result.map((node, index) => ({
    label: node.label,
    ...(node.path !== undefined ? { path: node.path } : {}),
    ...(index === result.length - 1 && node.representsLocation ? { current: true } : {}),
  }));
}

export function resolveBreadcrumbs(input: TrailInput): Crumb[] {
  // Same reason as caseRouteSuffix: compare the route the router matched, not the
  // literal spelling the user typed, or a case/encoding variant renders the screen
  // with no trail and therefore no way back.
  const suffix = caseRouteSuffix(input.pathname, input.caseKey);
  const path = suffix === null
    ? normalize(input.pathname)
    : `/cases/${input.caseKey}${suffix.toLowerCase()}`;
  // Pass the RAW pathname: crumbsFromPath does its own route-identity read and
  // preserves ID segments verbatim, which the lowercased `path` above would corrupt.
  const base = crumbsFromPath(input.pathname, input.caseKey, input.caseName);
  const matchedKey = path.match(/^\/cases\/([^/]+)(?=\/|$)/)?.[1];
  const validCase = !!input.caseKey && matchedKey === input.caseKey;
  let nodes: Node[] = base.map((crumb, index) => ({
    ...crumb,
    // Compare route identity, not raw text: the crumb href keeps the ID's spelling
    // while `path` is canonicalised, so a mixed-case or encoded ID would otherwise
    // never match and the current page would render as a clickable self-link.
    representsLocation: index === base.length - 1 && crumb.path !== undefined
      && routeIdentity(crumb.path) === routeIdentity(input.pathname),
  }));
  if (!validCase) return mergeNodes(nodes, []);

  const K = `/cases/${input.caseKey}`;
  const C = `${K}/collection`;
  const R = `${K}/documents/review`;
  const isCollection = path === C;
  const params = new URLSearchParams(input.search);
  let allowContributions = base.length > 2;

  if (isCollection) {
    if (!hasCollectionScope(input.search)) {
      return mergeNodes(nodes, []);
    }
    nodes = base.slice(0, 2).map(crumb => ({ ...crumb, representsLocation: false }));
    nodes.push({ label: 'Document Collection', path: C });
    return mergeNodes(nodes, []);
  } else if (path === R) {
    allowContributions = true;
    const direction = params.get('direction');
    const clean = new URLSearchParams(input.search);
    if (direction !== 'received' && direction !== 'outgoing') clean.delete('direction');
    // Card source defines entity levels. A production-mode batch can filter
    // the received table, but remains subordinate and never selects a collection.
    const selectedKey = input.reviewSource === 'production' ? 'collection' : 'batch';
    const selectedId = nonempty(clean.get(selectedKey));
    nodes[nodes.length - 1] = {
      label: 'Document Discovery',
      path: scopedHref(R, clean.toString(), ['direction']),
      representsLocation: !selectedId,
    };
    if (selectedId) {
      nodes.push({
        slot: 'production',
        representsLocation: true,
        label: input.reviewSource === 'production'
          && selectedId === 'all'
          && !nonempty(clean.get('custodian'))
          ? 'All collections'
          : 'Production',
        path: scopedHref(
          R,
          clean.toString(),
          input.reviewSource === 'production'
            ? ['direction', 'collection', 'custodian']
            : ['direction', 'batch'],
        ),
      });
    }
  } else if (path === `${K}/claims-matrix` && nonempty(params.get('pleading')) !== null) {
    // #1093 — one matrix per pleading, so the trail is Claims Matrix (the tile
    // picker) → this pleading. The parent crumb drops its `?pleading`, which is
    // what makes it the way back; the picker is the no-param half of the same
    // route. `?complaint` is deliberately not handled here: that is the old
    // screen, it keeps its own single crumb, and it stays the rollback target.
    allowContributions = true;
    nodes[nodes.length - 1] = {
      label: 'Claims Matrix',
      path: `${K}/claims-matrix`,
      representsLocation: false,
    };
    nodes.push({
      slot: 'pleading',
      // Placeholder until the screen contributes the short title, so the trail
      // is navigable on the very first paint rather than after the fetch.
      label: 'Pleading',
      path: scopedHref(`${K}/claims-matrix`, input.search, ['pleading']),
      representsLocation: true,
    });
  }

  const documentContributions = input.contributions.filter(
    contribution => contribution.slot === 'document' && contribution.label.trim(),
  );
  if (
    path === R
    && nodes.some(node => node.slot === 'production')
    && reviewDocumentMode(input.search, input.reviewSource) !== null
    && documentContributions.length === 1
  ) {
    // Placeholder label only — mergeNodes renames it from the contribution, which
    // keeps every crumb label flowing through one path and ignores contributed hrefs.
    nodes.push({ slot: 'document', label: 'Document', representsLocation: true });
  }

  return mergeNodes(nodes, allowContributions ? input.contributions : []);
}
